import React, { useMemo } from 'react';

const BackgroundLayer: React.FC = () => {
    // Generate random stars with memoization for performance
    const stars = useMemo(() => {
        // Main stars - medium brightness, varied sizes
        const mainStars = Array.from({ length: 60 }, (_, i) => ({
            id: i,
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            size: Math.random() * 2 + 1, // 1-3px
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${Math.random() * 3 + 2}s`, // 2-5s
            opacity: Math.random() * 0.3 + 0.15, // 0.15-0.45
            type: 'main' as const
        }));

        // Small dim background stars - creates depth
        const dimStars = Array.from({ length: 25 }, (_, i) => ({
            id: i + 60,
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            size: Math.random() * 1 + 0.5, // 0.5-1.5px
            animationDelay: `${Math.random() * 4}s`,
            animationDuration: `${Math.random() * 4 + 3}s`, // 3-7s
            opacity: Math.random() * 0.15 + 0.05, // 0.05-0.2 (very subtle)
            type: 'dim' as const
        }));

        // Bright featured stars - occasional bright twinkles
        const brightStars = Array.from({ length: 8 }, (_, i) => ({
            id: i + 85,
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            size: Math.random() * 1.5 + 2, // 2-3.5px
            animationDelay: `${Math.random() * 3}s`,
            animationDuration: `${Math.random() * 1.5 + 1}s`, // 1-2.5s (faster blink)
            opacity: Math.random() * 0.2 + 0.6, // 0.6-0.8 (brighter)
            type: 'bright' as const
        }));

        return [...mainStars, ...dimStars, ...brightStars];
    }, []);

    return (
        <div 
            className="fixed inset-0 z-0 pointer-events-none overflow-hidden"
            style={{ 
                contain: 'strict',
                // iOS Safari: Force GPU layer but avoid problematic blend modes
                WebkitTransform: 'translateZ(0)',
                transform: 'translateZ(0)'
            }}
        >
            {/* Deep base gradient - Saturated Royal Purple */}
            <div className="absolute inset-0 bg-gradient-to-b from-cosmic-950 via-cosmic-900 to-cosmic-950" />

            {/* Starfield */}
            <div className="absolute inset-0">
                {stars.map((star) => (
                    <div
                        key={star.id}
                        className={`absolute rounded-full bg-white ${star.type === 'bright' ? 'animate-pulse' : 'animate-twinkle'}`}
                        style={{
                            top: star.top,
                            left: star.left,
                            width: `${star.size}px`,
                            height: `${star.size}px`,
                            opacity: star.opacity,
                            animationDelay: star.animationDelay,
                            animationDuration: star.animationDuration,
                        }}
                    />
                ))}
            </div>

            {/* 
                Large atmospheric orbs - iOS Safari Fix
                Removed: mix-blend-screen (causes blank page on iOS Safari)
                Reduced: blur values from 150-200px to 80-100px for better iOS performance
            */}
            <div className="absolute top-[-20%] -left-[10%] w-[80%] h-[60%] bg-purple-500/20 blur-[80px] rounded-full animate-pulse" style={{ animationDuration: '8s' }} />
            <div className="absolute top-[10%] -right-[20%] w-[90%] h-[70%] bg-indigo-500/15 blur-[100px] rounded-full animate-pulse" style={{ animationDuration: '12s' }} />
            <div className="absolute top-[40%] -left-[30%] w-[100%] h-[80%] bg-fuchsia-600/15 blur-[100px] rounded-full" />
            <div className="absolute bottom-[-15%] -right-[15%] w-[80%] h-[60%] bg-purple-700/25 blur-[80px] rounded-full" />

            {/* 
                Grain overlay removed for iOS Safari compatibility
                External resource loading can fail silently on Safari
                mix-blend-overlay also causes issues on iOS Safari
            */}
        </div>
    );
};

export default BackgroundLayer;
